import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { reportStorageService, StoredReport } from '../services/ReportStorageService';
import { csvProcessingService } from '../services/CSVProcessingService';
import MarketingReport from '../components/MarketingReport';
import CCMarketingReport from '../components/reports/CCMarketingReport';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

/**
 * SavedReportsPage - Manage saved reports from localStorage
 *
 * Features:
 * - List all saved reports with metadata
 * - View individual reports with full MarketingReport/CCMarketingReport components
 * - Delete reports with confirmation
 * - Add ad spend to existing reports
 * - Export to PDF and print
 * - Storage usage information
 */
const SavedReportsPage: React.FC = () => {
  // ============================================================================
  // State Management
  // ============================================================================
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const viewId = searchParams.get('view');

  const [reports, setReports] = useState<StoredReport[]>([]);
  const [viewingReport, setViewingReport] = useState<StoredReport | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [addAdSpendId, setAddAdSpendId] = useState<string | null>(null);
  const [adSpendFile, setAdSpendFile] = useState<File | null>(null);
  const [isProcessingAdSpend, setIsProcessingAdSpend] = useState(false);
  const [storageInfo, setStorageInfo] = useState({ count: 0, sizeKB: 0 });
  const [error, setError] = useState<string | null>(null);

  const reportRef = useRef<HTMLDivElement>(null);

  // ============================================================================
  // Constants
  // ============================================================================
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const STORAGE_LIMIT_KB = 5120; // 5MB typical localStorage limit

  // ============================================================================
  // Data Loading
  // ============================================================================
  useEffect(() => {
    loadReports();
  }, []);

  useEffect(() => {
    if (viewId) {
      const report = reportStorageService.getReport(viewId);
      if (report) {
        setViewingReport(report);
      } else {
        setError('Report not found');
        setSearchParams({});
      }
    } else {
      setViewingReport(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewId]);

  const loadReports = () => {
    const loadedReports = reportStorageService.getReports();
    const info = reportStorageService.getStorageInfo();
    setReports(loadedReports);
    setStorageInfo(info);
  };

  // ============================================================================
  // Utility Functions
  // ============================================================================
  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);

  const formatDate = (isoString: string) => {
    const date = new Date(isoString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const getPeriodDisplay = (period: { month: number; year: number }) => {
    return `${monthNames[period.month - 1]} ${period.year}`;
  };

  // ============================================================================
  // Report Actions
  // ============================================================================
  const handleViewReport = (reportId: string) => {
    setSearchParams({ view: reportId });
  };

  const handleBackToList = () => {
    setSearchParams({});
    setError(null);
  };

  const handleDeleteReport = (reportId: string) => {
    const success = reportStorageService.deleteReport(reportId);
    if (success) {
      loadReports();
      if (viewingReport?.id === reportId) {
        handleBackToList();
      }
    }
    setDeleteConfirmId(null);
  };

  const handlePrint = () => {
    window.print();
  };

  const generatePDF = async () => {
    if (!reportRef.current || !viewingReport) return;

    try {
      const monthName = monthNames[viewingReport.period.month - 1];
      const prefix = viewingReport.reportType === 'cody'
        ? 'ROI_Cody_Direct_Sales'
        : 'ROI_Marketing_Report';
      const fileName = `${prefix}_${monthName}_${viewingReport.period.year}.pdf`;

      const canvas = await html2canvas(reportRef.current, {
        scale: 1.5,
        useCORS: true,
        logging: false,
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4' });
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const canvasRatio = canvas.height / canvas.width;
      const contentWidth = pdfWidth;
      const contentHeight = contentWidth * canvasRatio;

      let heightLeft = contentHeight;
      let position = 0;
      let page = 1;

      pdf.addImage(imgData, 'PNG', 0, position, contentWidth, contentHeight);
      heightLeft -= pdfHeight;

      while (heightLeft > 0) {
        position = -pdfHeight * page;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, contentWidth, contentHeight);
        heightLeft -= pdfHeight;
        page++;
      }

      for (let i = 0; i < page; i++) {
        pdf.setPage(i + 1);
        pdf.setFontSize(8);
        pdf.setTextColor(100, 100, 100);
        pdf.text(`ROI Exteriors Marketing Report - Page ${i + 1} of ${page}`, 10, pdfHeight - 5);
      }

      pdf.save(fileName);
    } catch (error) {
      console.error('Error generating PDF:', error);
      setError('Failed to generate PDF. Please try again.');
    }
  };

  // ============================================================================
  // Ad Spend Management
  // ============================================================================
  const handleAdSpendFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setAdSpendFile(file);
    }
  };

  const handleAddAdSpend = async (reportId: string) => {
    if (!adSpendFile) return;

    setIsProcessingAdSpend(true);
    setError(null);

    try {
      // Parse the ad spend CSV
      const adSpendData = await csvProcessingService.parseAdSpendFromFile(adSpendFile);

      if (adSpendData.length === 0) {
        setError('No valid ad spend data found in the CSV file');
        setIsProcessingAdSpend(false);
        return;
      }

      // Get the current report
      const report = reportStorageService.getReport(reportId);
      if (!report) {
        setError('Report not found');
        setIsProcessingAdSpend(false);
        return;
      }

      // Calculate total new ad spend
      const totalNewAdSpend = adSpendData.reduce((sum, item) => sum + item.spend, 0);

      // Update the report data
      const updatedData = {
        ...report.data,
        adSpendBySource: [...report.data.adSpendBySource, ...adSpendData],
        kpis: {
          ...report.data.kpis,
          adSpend: report.data.kpis.adSpend + totalNewAdSpend,
          netProfit: report.data.kpis.netProfit - totalNewAdSpend,
          netProfitPercent: report.data.kpis.totalSales > 0
            ? (report.data.kpis.netProfit - totalNewAdSpend) / report.data.kpis.totalSales
            : 0
        }
      };

      // Delete the old report and save the updated one
      reportStorageService.deleteReport(reportId);
      const newId = reportStorageService.saveReport(
        report.title,
        report.reportType,
        report.period,
        updatedData,
        report.generationMode
      );

      // Reload reports and update viewing report if necessary
      loadReports();
      if (viewingReport?.id === reportId) {
        const updatedReport = reportStorageService.getReport(newId);
        if (updatedReport) {
          setViewingReport(updatedReport);
          setSearchParams({ view: newId });
        }
      }

      // Clean up
      setAddAdSpendId(null);
      setAdSpendFile(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to process ad spend CSV');
    } finally {
      setIsProcessingAdSpend(false);
    }
  };

  // ============================================================================
  // Report List View
  // ============================================================================
  const renderReportsList = () => (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Saved Reports
        </h1>
        <p className="text-gray-600">
          Manage and view your generated marketing reports
        </p>
      </div>

      {/* Storage Info */}
      <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
          <div className="text-sm text-blue-600 font-medium mb-1">Total Reports</div>
          <div className="text-2xl font-bold text-blue-900">{storageInfo.count}</div>
        </div>

        <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
          <div className="text-sm text-purple-600 font-medium mb-1">Storage Used</div>
          <div className="text-2xl font-bold text-purple-900">
            {storageInfo.sizeKB.toFixed(2)} KB
          </div>
        </div>

        <div className="bg-green-50 p-4 rounded-lg border border-green-200">
          <div className="text-sm text-green-600 font-medium mb-1">Available Space</div>
          <div className="text-2xl font-bold text-green-900">
            {((STORAGE_LIMIT_KB - storageInfo.sizeKB) / STORAGE_LIMIT_KB * 100).toFixed(1)}%
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-start">
            <span className="text-red-600 mr-2">✗</span>
            <div className="text-sm text-red-800">{error}</div>
          </div>
        </div>
      )}

      {/* Reports List */}
      {reports.length === 0 ? (
        <div className="bg-white rounded-lg shadow-md p-12 text-center">
          <div className="text-6xl mb-4">📊</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            No Saved Reports Yet
          </h2>
          <p className="text-gray-600 mb-6">
            Generate your first marketing report to get started
          </p>
          <button
            onClick={() => navigate('/generate-report')}
            className="px-6 py-3 rounded-md font-medium bg-blue-600 text-white hover:bg-blue-700 transition-colors"
          >
            Generate Report
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {reports.map((report) => (
            <div
              key={report.id}
              className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow"
            >
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                {/* Report Info */}
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-bold text-gray-900">
                      {report.title}
                    </h3>
                    <span
                      className={`
                        px-3 py-1 rounded-full text-xs font-medium
                        ${report.reportType === 'cody'
                          ? 'bg-orange-100 text-orange-700'
                          : 'bg-blue-100 text-blue-700'
                        }
                      `}
                    >
                      {report.reportType === 'cody' ? 'Cody' : 'Standard'}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Period:</span>
                      <div className="font-medium text-gray-900">
                        {getPeriodDisplay(report.period)}
                      </div>
                    </div>

                    <div>
                      <span className="text-gray-500">Jobs:</span>
                      <div className="font-medium text-gray-900">
                        {report.data.jobData.length}
                      </div>
                    </div>

                    <div>
                      <span className="text-gray-500">Total Sales:</span>
                      <div className="font-medium text-gray-900">
                        {formatCurrency(report.data.kpis.totalSales)}
                      </div>
                    </div>

                    <div>
                      <span className="text-gray-500">Net Profit:</span>
                      <div className={`font-medium ${
                        report.data.kpis.netProfit >= 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {formatCurrency(report.data.kpis.netProfit)}
                      </div>
                    </div>
                  </div>

                  <div className="mt-2 flex items-center gap-4 text-xs text-gray-500">
                    <div>
                      Ad Spend: {report.data.kpis.adSpend > 0
                        ? formatCurrency(report.data.kpis.adSpend)
                        : 'Not included'
                      }
                    </div>
                    <div>
                      Saved: {formatDate(report.createdAt)}
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => handleViewReport(report.id)}
                    className="px-4 py-2 rounded-md font-medium bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                  >
                    View
                  </button>

                  {report.reportType === 'standard' && (
                    <button
                      onClick={() => setAddAdSpendId(report.id)}
                      className="px-4 py-2 rounded-md font-medium bg-green-600 text-white hover:bg-green-700 transition-colors"
                    >
                      Add Ad Spend
                    </button>
                  )}

                  <button
                    onClick={() => setDeleteConfirmId(report.id)}
                    className="px-4 py-2 rounded-md font-medium bg-red-600 text-white hover:bg-red-700 transition-colors"
                  >
                    Delete
                  </button>
                </div>
              </div>

              {/* Add Ad Spend Form */}
              {addAdSpendId === report.id && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <h4 className="font-medium text-gray-900 mb-3">Add Ad Spend Data</h4>
                  <div className="flex items-end gap-3">
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Ad Spend CSV File
                      </label>
                      <input
                        type="file"
                        accept=".csv"
                        onChange={handleAdSpendFileChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        disabled={isProcessingAdSpend}
                      />
                    </div>
                    <button
                      onClick={() => handleAddAdSpend(report.id)}
                      disabled={!adSpendFile || isProcessingAdSpend}
                      className={`
                        px-4 py-2 rounded-md font-medium transition-colors
                        ${adSpendFile && !isProcessingAdSpend
                          ? 'bg-green-600 text-white hover:bg-green-700'
                          : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        }
                      `}
                    >
                      {isProcessingAdSpend ? 'Processing...' : 'Add'}
                    </button>
                    <button
                      onClick={() => {
                        setAddAdSpendId(null);
                        setAdSpendFile(null);
                      }}
                      className="px-4 py-2 rounded-md font-medium bg-gray-200 text-gray-700 hover:bg-gray-300 transition-colors"
                      disabled={isProcessingAdSpend}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* Delete Confirmation */}
              {deleteConfirmId === report.id && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-gray-700">
                      Are you sure you want to delete this report? This action cannot be undone.
                    </p>
                    <div className="flex gap-2 ml-4">
                      <button
                        onClick={() => handleDeleteReport(report.id)}
                        className="px-4 py-2 rounded-md font-medium bg-red-600 text-white hover:bg-red-700 transition-colors"
                      >
                        Confirm Delete
                      </button>
                      <button
                        onClick={() => setDeleteConfirmId(null)}
                        className="px-4 py-2 rounded-md font-medium bg-gray-200 text-gray-700 hover:bg-gray-300 transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // ============================================================================
  // Report View
  // ============================================================================
  const renderReportView = () => {
    if (!viewingReport) return null;

    return (
      <div className="max-w-7xl mx-auto">
        {/* Action Bar */}
        <div className="bg-white rounded-lg shadow-md p-4 mb-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button
              onClick={handleBackToList}
              className="px-4 py-2 rounded-md font-medium bg-gray-200 text-gray-700 hover:bg-gray-300 transition-colors"
            >
              ← Back to List
            </button>

            <div>
              <h2 className="text-xl font-bold text-gray-900">
                {viewingReport.title}
              </h2>
              <div className="text-sm text-gray-500">
                {getPeriodDisplay(viewingReport.period)} · {viewingReport.data.jobData.length} jobs
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              onClick={handlePrint}
              className="px-4 py-2 rounded-md font-medium bg-gray-200 text-gray-700 hover:bg-gray-300 transition-colors"
            >
              Print
            </button>

            <button
              onClick={generatePDF}
              className="px-4 py-2 rounded-md font-medium bg-green-600 text-white hover:bg-green-700 transition-colors"
            >
              Export PDF
            </button>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-start">
              <span className="text-red-600 mr-2">✗</span>
              <div className="text-sm text-red-800">{error}</div>
            </div>
          </div>
        )}

        {/* Report Content */}
        <div ref={reportRef}>
          {viewingReport.reportType === 'standard' && (
            <MarketingReport data={viewingReport.data} hideHeader />
          )}
          {viewingReport.reportType === 'cody' && (
            <CCMarketingReport data={viewingReport.data} hideHeader />
          )}
        </div>
      </div>
    );
  };

  // ============================================================================
  // Main Render
  // ============================================================================
  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      {viewingReport ? renderReportView() : renderReportsList()}
    </div>
  );
};

export default SavedReportsPage;
